import React, { useCallback, useContext, useState } from 'react';
import GameContext from 'contexts/GameContext';
import { useTableSelector } from 'hooks/useTableSelector';
import _ from 'lodash';
import { Player } from 'reducers/currentTable';
import useGameService from 'services/GameService';
import { convertToNumber } from 'utils/functions';

import FoldConfirmationModal from 'components/UserActions/FoldConfirmationModal';

import ActionButton from './ActionButton';

interface FoldProps {
  disabled: boolean;
  /** See PokerActions' submitAction - carries the re-send closure and the action name. */
  submitAction: (send: (stamp: string) => void, name: string) => void;
  /** The emit was not acked in time (or could not be sent). PokerActions decides what happens. */
  onAckTimeout: () => void;
  triggerHideServerTimeout: () => void;
}

const Fold = ({ disabled, submitAction, onAckTimeout, triggerHideServerTimeout }: FoldProps) => {
  const { dontAskAgain } = useContext(GameContext);
  const { hand, highestBetPlayer } = useTableSelector((table) => table.game);
  const { fold, check } = useGameService();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const players = useTableSelector((table) => table.players);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const playersLBet = convertToNumber(players[+seatId]?.betDisplay);
  const highestPlayerLBet = convertToNumber(highestBetPlayer?.betDisplay);
  const gameHand = +hand;

  const isBB = useTableSelector((table) => table.currentPlayer.isBB);
  const isPostedBlind = useTableSelector((table) => table.currentPlayer.isPostedBlind);
  const isStraddle = useTableSelector((table) => table.currentPlayer.isStraddle);

  const isCannotCheck = useCallback(() => {
    return playersLBet < highestPlayerLBet && highestPlayerLBet > 0;
  }, [highestPlayerLBet, playersLBet]);

  const someRaise = (players: Player[]) => {
    return _.some(players ?? [], (player) =>
      _.includes(['raise', 'allin', 'straddle'], player?.action?.toLowerCase())
    );
  };
  const someBet = (players: Player[]) => {
    return _.some(players ?? [], (player) => _.includes(['bet'], player?.action?.toLowerCase()));
  };

  const isCall = useCallback(() => {
    if (
      playersLBet !== highestPlayerLBet &&
      gameHand === 5 &&
      ((!isBB && !isPostedBlind && !isStraddle) ||
        (isBB && someRaise(players) && highestPlayerLBet > playersLBet) ||
        (isPostedBlind && isCannotCheck()))
    ) {
      return true;
    }
    if (gameHand > 5 && (someRaise(players) || someBet(players))) {
      return true;
    }
    return false;
  }, [gameHand, isBB, isCannotCheck, isPostedBlind, isStraddle, players]);

  const confirmation = () => {
    if (isCall() || dontAskAgain) {
      submitAction((stamp) => fold(stamp, triggerHideServerTimeout, onAckTimeout), 'fold');
    } else {
      setShowConfirmationModal(true);
    }
  };

  const confirmationAnswer = (answer: any) => {
    switch (answer) {
      case 1:
        submitAction((stamp) => fold(stamp, triggerHideServerTimeout, onAckTimeout), 'fold');
        break;
      case 2:
        submitAction((stamp) => check(stamp, triggerHideServerTimeout, onAckTimeout), 'check');
        break;
    }
    setShowConfirmationModal(false);
  };

  return (
    <>
      <ActionButton variant='fold' onClick={confirmation} disabled={disabled} icon='fold'>
        Fold
      </ActionButton>
      {showConfirmationModal && (
        <FoldConfirmationModal
          confirmationAnswer={confirmationAnswer}
          // Dismissed without answering: close the prompt and nothing else. No
          // action is submitted, so the turn stays exactly where it was and the
          // player can Fold / Check / Call from the bar as if they had never
          // opened this. The server-side turn clock was never paused for the
          // prompt, so it simply keeps running, as it does when the prompt is
          // skipped entirely (see `confirmation` above).
          onClose={() => setShowConfirmationModal(false)}
        />
      )}
    </>
  );
};

export default Fold;
